import { useNavigate } from 'react-router-dom';
import { useBookings } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import CustomerBottomNav from '../../components/common/CustomerBottomNav';
import { 
  ArrowLeft, Clock, CheckCircle, XCircle, ChevronRight, MapPin, Calendar
} from 'lucide-react';

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  accepted: { label: 'Accepted', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  on_the_way: { label: 'On the Way', color: 'bg-purple-100 text-purple-800', icon: Clock },
  arrived: { label: 'Arrived', color: 'bg-indigo-100 text-indigo-800', icon: MapPin },
  in_progress: { label: 'In Progress', color: 'bg-orange-100 text-orange-800', icon: Clock },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800', icon: XCircle },
  finding_provider: { label: 'Finding Provider', color: 'bg-gray-100 text-gray-800', icon: Clock },
};

export default function CustomerBookings() {
  const navigate = useNavigate();
  const { bookings, isLoading } = useBookings();

  const activeBookings = bookings.filter(b => 
    !['completed', 'cancelled'].includes(b.status)
  );
  const historyBookings = bookings.filter(b => 
    ['completed', 'cancelled'].includes(b.status)
  );

  const BookingCard = ({ booking }) => {
    const status = statusConfig[booking.status] || statusConfig.pending;
    const StatusIcon = status.icon;
    
    return (
      <button
        className="w-full bg-white rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow text-left border-l-4 border-l-amber-500"
        onClick={() => navigate(`/booking/${booking.id}`)}
        data-testid={`booking-${booking.id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="font-semibold text-slate-900">#{booking.id.slice(0, 8).toUpperCase()}</p>
            <p className="text-sm text-slate-500">{booking.scheduled_date} • {booking.scheduled_time}</p>
          </div>
          <span className={`status-badge ${status.color}`}>
            <StatusIcon className="w-3 h-3 mr-1" />
            {status.label}
          </span>
        </div>
        
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-4 h-4 text-slate-400" />
          <p className="text-sm text-slate-600 truncate">{booking.address?.full_address}</p>
        </div>
        
        <div className="flex items-center justify-between pt-3 border-t border-stone-100">
          <span className="text-lg font-bold text-amber-600">₱{booking.total_price}</span>
          <ChevronRight className="w-5 h-5 text-slate-400" />
        </div>
      </button>
    );
  };

  return (
    <div className="page-container">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 px-4 py-4 sticky top-0 z-40">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/home')}
            className="w-10 h-10 rounded-full hover:bg-stone-100 flex items-center justify-center"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <h1 className="text-xl font-bold text-slate-900 font-[Poppins]">My Bookings</h1>
        </div>
      </header>

      <main className="px-4 py-6">
        <Tabs defaultValue="active" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="active" data-testid="active-tab">
              Active ({activeBookings.length})
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab">
              History ({historyBookings.length})
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="active">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={`skeleton-active-${i}`} className="animate-pulse bg-stone-200 rounded-2xl h-32" />
                ))}
              </div>
            ) : activeBookings.length > 0 ? (
              <div className="space-y-4">
                {activeBookings.map(booking => (
                  <BookingCard key={booking.id} booking={booking} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No active bookings</p>
                <Button onClick={() => navigate('/home')} data-testid="book-now-btn">
                  Book a Service
                </Button>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="history">
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={`skeleton-history-${i}`} className="animate-pulse bg-stone-200 rounded-2xl h-32" />
                ))}
              </div>
            ) : historyBookings.length > 0 ? (
              <div className="space-y-4">
                {historyBookings.map(booking => (
                  <BookingCard key={booking.id} booking={booking} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <p className="text-slate-500">No booking history yet</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Bottom Navigation */}
      <CustomerBottomNav />
    </div>
  );
}
